import { destinationById } from '../../../../lib/publishing/destinations';
import type { ComposerState, ComposerTarget } from '../../../publishing/composerState';
import { selectedDestinationIds } from '../../../publishing/composerState';
import type { PostDestinationId } from '../../../../lib/publishing/destinations';
import { UploadMediaButton } from './UploadMediaButton';

// A different caption and/or different photos or videos for ONE placement. The
// model is the publishing composer's own (composerState): a tick-box beside each
// value, so unticking stops the override applying WITHOUT losing what was typed,
// and only ticked overrides are ever sent. A Story has no caption, so it offers
// the media override only. ⚠️ Nothing here calls AI: drafting writes the BASE
// caption and never an override.

export function PlacementOverrides({
  state,
  setTarget,
  onUpload,
  uploading,
}: {
  state: ComposerState;
  setTarget: (id: PostDestinationId, patch: Partial<ComposerTarget>) => void;
  onUpload: (id: PostDestinationId, files: FileList | null) => void;
  uploading: boolean;
}) {
  const ids = selectedDestinationIds(state);
  if (ids.length === 0) return null;
  return (
    <section className="space-y-2 rounded-md border border-border p-3" aria-label="Different for one platform" data-testid="placement-overrides">
      <h3 className="text-sm font-semibold text-foreground">Different for one platform (optional)</h3>
      <p className="text-xs text-muted-foreground">
        Everything above goes to every platform you pick. Tick a box below to use a different caption or different photos or
        videos for just that one.
      </p>
      {ids.map((id) => {
        const d = destinationById(id);
        const t = state.targets[id];
        const isStory = d.placement === 'story';
        return (
          <details key={id} className="rounded border border-border p-2" data-testid={`override-${id}`}>
            <summary className="cursor-pointer text-sm font-medium text-foreground">
              {d.label}
              {d.placement === 'story' ? ' Story' : ''}
              {!isStory && t.overrideCaption && t.customContent.trim() !== '' ? ' · own caption' : ''}
              {t.overrideMedia && t.customMedia.length > 0 ? ' · own media' : ''}
            </summary>
            <div className="mt-2 space-y-3 text-sm">
              {!isStory && (
                <div>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={t.overrideCaption}
                      onChange={(e) => setTarget(id, { overrideCaption: e.target.checked })}
                    />
                    Use a different caption for {d.label}
                  </label>
                  {t.overrideCaption && (
                    <textarea
                      aria-label={`${d.label} caption`}
                      className="mt-1 w-full rounded-md border border-input bg-background p-2 text-sm"
                      rows={4}
                      value={t.customContent}
                      onChange={(e) => setTarget(id, { customContent: e.target.value })}
                    />
                  )}
                  {t.overrideCaption && t.customContent.trim() === '' && (
                    <p className="text-xs text-muted-foreground">Empty, so {d.label} will use the main caption.</p>
                  )}
                </div>
              )}
              <div>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={t.overrideMedia}
                    onChange={(e) => setTarget(id, { overrideMedia: e.target.checked })}
                  />
                  Use different photos or videos for {d.label}
                </label>
                {t.overrideMedia && (
                  <div className="mt-1 space-y-2">
                    <div className="flex flex-wrap items-center gap-3">
                      <UploadMediaButton
                        variant="outline"
                        label={`Add photos or videos for ${d.label}`}
                        uploading={uploading}
                        onFiles={(f) => onUpload(id, f)}
                      />
                    </div>
                    {t.customMedia.length > 0 ? (
                      <div className="flex flex-wrap items-center gap-3">
                        {t.customMedia.map((m, i) => (
                          <div key={m.url} className="relative">
                            {m.type === 'image' ? (
                              <img src={m.url} alt={m.altText ?? ''} className="h-16 w-16 rounded object-cover" />
                            ) : (
                              <video src={m.url} className="h-16 w-16 rounded object-cover" />
                            )}
                            <button
                              type="button"
                              className="absolute -right-1 -top-1 rounded-full bg-background px-1 text-xs shadow"
                              aria-label={`Remove from ${d.label}`}
                              onClick={() => setTarget(id, { customMedia: t.customMedia.filter((_, j) => j !== i) })}
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground">None added, so {d.label} will use the main photos and videos.</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </details>
        );
      })}
    </section>
  );
}
